import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Edit2, User, Settings, Mic, Clock, Coins } from 'lucide-react';
import { QuestionnaireData } from '@/types/category';
import { SessionCostPreview } from '@/components/session/SessionCostPreview';

interface ReviewStepProps {
  formData: Partial<QuestionnaireData>;
  categoryName: string;
  onEdit: (stepIndex: number) => void;
  sessionType: 'individual' | 'group';
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  formData,
  categoryName,
  onEdit,
  sessionType
}) => {
  const formatSessionLength = (sessionLength?: string | number) => {
    if (!sessionLength) return 'Not selected';
    // Handle both string ("X minutes") and number (seconds) formats
    if (typeof sessionLength === 'string') {
      return sessionLength;
    }
    const minutes = sessionLength / 60;
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  const formatList = (items?: string[]) => {
    if (!items || items.length === 0) return 'None selected';
    return items.join(', ');
  };

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 mb-3">
          <CheckCircle className="h-6 w-6 text-emerald-600" />
        </div>
        <h3 className="text-lg font-semibold mb-1">Review Your Session</h3>
        <p className="text-sm text-muted-foreground">
          Review your selections before creating your {categoryName} session
        </p>
      </div>

      {/* Personal/Group Information */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              {sessionType === 'individual' ? 'Personal Information' : 'Group Information'}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(0)}
              className="h-7 px-2 text-xs"
            >
              <Edit2 className="h-3 w-3 mr-1" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {sessionType === 'individual' ? (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Name:</span>
                <span className="font-medium">
                  {formData.firstName || 'Not provided'} {formData.lastName || ''}
                </span>
              </div>
              {formData.age && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Age:</span>
                  <span className="font-medium">{formData.age}</span>
                </div>
              )}
              {formData.occupation && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Occupation:</span>
                  <span className="font-medium">{formData.occupation}</span>
                </div>
              )}
              {formData.location && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Location:</span>
                  <span className="font-medium">{formData.location}</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Group Name:</span>
                <span className="font-medium">{formData.groupName || 'Not provided'}</span>
              </div>
              {formData.groupSize && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Group Size:</span>
                  <span className="font-medium">{formData.groupSize} participants</span>
                </div>
              )}
            </>
          )}
          {formData.usePersonalBiography && (
            <div className="flex items-center justify-between text-sm pt-2 border-t">
              <span className="text-muted-foreground">Biography:</span>
              <span className="font-medium text-emerald-600">Will be used</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Session Details */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Session Details
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(1)}
              className="h-7 px-2 text-xs"
            >
              <Edit2 className="h-3 w-3 mr-1" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {formData.environment && (
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground">Environment:</span>
              <p className="text-sm font-medium">{formData.environment}</p>
            </div>
          )}

          {/* Category-specific fields */}
          {formData.specificFocus && (
            <div className="space-y-1 pt-2 border-t">
              <span className="text-sm text-muted-foreground">What's bothering you:</span>
              <p className="text-sm font-medium">{formData.specificFocus}</p>
            </div>
          )}
          {formData.deeperReason && (
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground">Why:</span>
              <p className="text-sm font-medium">{formData.deeperReason}</p>
            </div>
          )}
          {formData.groupFrustration && (
            <div className="space-y-1 pt-2 border-t">
              <span className="text-sm text-muted-foreground">Group frustration:</span>
              <p className="text-sm font-medium">{formData.groupFrustration}</p>
            </div>
          )}
          {formData.massageType && (
            <div className="flex justify-between text-sm pt-2 border-t">
              <span className="text-muted-foreground">Massage Type:</span>
              <span className="font-medium">{formData.massageType}</span>
            </div>
          )}
          {formData.targetBodyParts && formData.targetBodyParts.length > 0 && (
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground">Target Areas:</span>
              <p className="text-sm font-medium">{formatList(formData.targetBodyParts)}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Voice & Audio Preferences */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Mic className="h-4 w-4" />
              Voice & Audio
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(2)}
              className="h-7 px-2 text-xs"
            >
              <Edit2 className="h-3 w-3 mr-1" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Session Length:</span>
            <span className="font-medium flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatSessionLength(formData.sessionLength)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Voice:</span>
            <span className="font-medium capitalize">
              {formData.voiceGender || 'Not selected'}
              {formData.specific_voice_id && ' (Custom)'}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Background Music:</span>
            <span className="font-medium">
              {formData.backgroundMusicSelection || 'None'}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Background Sounds:</span>
            <span className="font-medium">
              {formData.backgroundSounds ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Session Cost Preview */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Coins className="h-4 w-4" />
            Session Cost
          </CardTitle>
        </CardHeader>
        <CardContent>
          <SessionCostPreview
            durationMinutes={parseSessionLengthToMinutes(formData.sessionLength)}
            show={true}
          />
        </CardContent>
      </Card>

      {/* Ready Message */}
      <Card className="border-emerald-200 bg-gradient-to-r from-white to-emerald-50/30">
        <CardContent className="pt-6 text-center">
          <CheckCircle className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
          <p className="text-sm font-medium mb-1">Everything looks great!</p>
          <p className="text-xs text-muted-foreground">
            Click "Create Session" below to generate your personalized {categoryName} session
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Parse session length to minutes from various formats
 */
function parseSessionLengthToMinutes(sessionLength?: string | number): number {
  if (!sessionLength) return 5; // Default 5 minutes

  if (typeof sessionLength === 'number') {
    // If it looks like seconds (> 60), convert to minutes
    return sessionLength > 60 ? sessionLength / 60 : sessionLength;
  }

  if (typeof sessionLength === 'string') {
    // Handle "X minutes" format
    const match = sessionLength.match(/(\d+)\s*min/i);
    if (match) {
      return parseInt(match[1], 10);
    }
    // Handle plain number string
    const num = parseInt(sessionLength, 10);
    if (!isNaN(num)) {
      return num > 60 ? num / 60 : num;
    }
  }

  return 5; // Default
}